import {
  Animate,
  AnimateIn,
  Container,
  Grid,
  GridColumn,
  Inline,
  Stack,
  Text,
} from 'epic-fortnite-react';
import {useResponsiveBreakpoints} from 'epic-fortnite-react/hooks/useResponsiveBreakpoints';
import React from 'react';
import styled from 'styled-components';
import root from 'window-or-global';
import epicTracking from 'epic-tracking';
import {WatchVideo} from '../common/WatchVideo';
import {CoreSection} from '../blocks/CoreSection';

import CTAButton from '../../components/common/CTAButton';

import {CoreSectionProps, FunctionalThemeProps} from './types';

export interface CtaProps {
  isCtaLinkExternal?: boolean;
  ctaString: string;
  ctaLink: string;
  disabled?: boolean;
  eventTracking?: {
    interactionType?: string;
    eventAction?: string;
    eventLabel?: string;
    eventValue?: string;
    eventCategory?: string;
  };
  textOnly?: boolean;
  theme?:
    | 'teal'
    | 'green'
    | 'black'
    | 'pink'
    | 'yellow'
    | 'blackYellow'
    | 'brightYellow'
    | undefined;
}

export interface TextSectionProps {
  cta?: CtaProps;
  groupCta?: CtaProps[];
  headerColor?: string;
  legalText?: string;
  legalTextColor?: string;
  legalTextLine2?: string;
  textAlignment?: 'center' | 'left' | 'right';
  textContent?: string;
  textContentColor?: string;
  textHeader?: string;
  trailerConfig?: {
    text?: string;
    textColor?: string;
    videoId?: string;
    isActive?: boolean;
  };
}

interface ImageProps {
  heroBackgroundImage?: string;
  heroImage?: string;
  heroImageAltText?: string;
  imageAnimates?: boolean;
  overlapHeroTop?: boolean;
  snapHeroToBottom?: boolean;
  snapHeroToTop?: boolean;
}

export interface SideBySideProps extends ImageProps {
  coreSectionProps?: CoreSectionProps;
  layout?:
    | 'imageLeft' // deprecated with fallback
    | 'imageRight' // deprecated with fallback
    | 'mobileTopDesktopLeft'
    | 'mobileTopDesktopRight'
    | 'mobileBottomDesktopLeft'
    | 'mobileBottomDesktopRight';
  textSection?: TextSectionProps;
  theme?: FunctionalThemeProps;
}

const StyledImagePlaceholder = styled.div`
  aspect-ratio: 1/1;
  width: 100%;
`;

interface SideBySideImageContainerProps {
  $overlapHeroTop: string;
  $snapHeroToBottom?: boolean;
  $snapHeroToTop?: boolean;
}

const SideBySideImageContainer = styled.div<SideBySideImageContainerProps>`
  margin-bottom: ${({$snapHeroToBottom}) =>
    `calc(${$snapHeroToBottom ? 'var(--section__padding)' : '0px'} * -1)`};
  margin-top: ${({$snapHeroToTop}) =>
    `calc(${$snapHeroToTop ? 'var(--section__padding)' : '0px'} * -1)`};
  position: relative;
  width: 100%;

  @media (max-width: 899px) {
    max-width: 400px;
  }

  @media (min-width: 900px) {
    margin-top: ${({$snapHeroToTop, $overlapHeroTop}) =>
      `calc((${$overlapHeroTop || $snapHeroToTop ? 'var(--section__padding)' : '0px'} + ${
        $overlapHeroTop ? $overlapHeroTop : '0px'
      }) * -1)`};
  }

  img {
    width: 100%;
  }
`;

const SideBySideBackgroundGlow = styled.div`
  background-color: var(--bundle-theme-color);
  border-radius: 50%;
  filter: blur(50px);
  height: 75%;
  left: 50%;
  opacity: 0.3;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 75%;
  z-index: -1;
`;

const SideBySideBackgroundImage = styled.img`
  left: 0;
  position: absolute;
  top: 0;
  z-index: -1;
`;

const SideBySideImage = styled.img<{imageAnimates?: boolean}>`
  position: relative;
  z-index: 1;
`;

interface LegalProps {
  color: string;
}

const LegalTextContainer = styled.div<LegalProps>`
  padding-left: var(--container__padding);
  padding-right: var(--container__padding);
  color: ${({color}) => color || '#F8FF01'};
  width: 100%;
  bottom: 10px;
  text-align: center;
  position: absolute;
  z-index: 2;

  p {
    margin: 0;
  }
`;

const trackCtaClick = (item) => {
  if (item.eventTracking && root._epicTracking) {
    const {
      interactionType = '',
      eventAction = '',
      eventLabel = '',
      eventValue = '',
      eventCategory = '',
    } = item.eventTracking;

    epicTracking.trackEvent('Interaction', {
      interactionType,
      eventAction,
      eventLabel,
      eventValue,
      eventCategory,
    });
  }
};

const Image = ({
  heroBackgroundImage,
  heroImage,
  heroImageAltText,
  imageAnimates,
  overlapHeroTop,
  snapHeroToBottom,
  snapHeroToTop,
}: ImageProps) => {
  return (
    <SideBySideImageContainer
      $overlapHeroTop={`${overlapHeroTop}`}
      $snapHeroToBottom={snapHeroToBottom}
      $snapHeroToTop={snapHeroToTop}>
      {heroImage ? (
        <AnimateIn>
          {heroBackgroundImage ? (
            <SideBySideBackgroundImage alt="" src={heroBackgroundImage} />
          ) : (
            <SideBySideBackgroundGlow />
          )}
          {imageAnimates ? (
            <Animate delay={Math.floor(Math.random() * (4 - 1 + 1) + 1)}>
              <SideBySideImage alt={heroImageAltText ? heroImageAltText : ''} src={heroImage} />
            </Animate>
          ) : (
            <SideBySideImage alt={heroImageAltText ? heroImageAltText : ''} src={heroImage} />
          )}
        </AnimateIn>
      ) : (
        <StyledImagePlaceholder />
      )}
    </SideBySideImageContainer>
  );
};

const TextSectionCTA = ({...props}: CtaProps) => {
  return (
    <CTAButton
      ctaConfig={{
        ...props,
        onExternalClickHandler: () => {
          trackCtaClick(props.eventTracking);
        },
      }}
      theme={props.theme ? props.theme : 'brightYellow'}
    />
  );
};

const TextSection = ({
  cta,
  groupCta,
  headerColor,
  legalText,
  legalTextColor,
  legalTextLine2,
  textAlignment,
  textContent,
  textContentColor,
  textHeader,
  trailerConfig,
}: TextSectionProps) => {
  const align = useResponsiveBreakpoints([{center: 900}, {left: 901}]);

  return (
    <Stack align={align.breakSize === 'center' ? 'center' : 'left'} animateIn={true} gap="lg">
      {textHeader && (
        <Text
          align={align.breakSize === 'center' ? 'center' : 'left'}
          as="h2"
          color={`${headerColor ? headerColor : 'var(--bundle-text-color--body)'}`}
          lineLength="sm"
          size="2xl"
          transform="uppercase">
          {textHeader}
        </Text>
      )}
      {textContent && (
        <Text
          align={align.breakSize === 'center' ? 'center' : 'left'}
          color={`${textContentColor ? textContentColor : 'var(--bundle-text-color--body)'}`}
          lineLength="sm"
          size="lg"
          transform="uppercase">
          {textContent}
        </Text>
      )}
      {cta?.ctaString && !cta?.disabled && <TextSectionCTA {...cta} />}
      {groupCta && (
        <Inline align="center" gap="xs" justify="center">
          {groupCta?.map((cta) => cta?.ctaString && !cta?.disabled && <TextSectionCTA {...cta} />)}
        </Inline>
      )}
      {trailerConfig?.videoId && trailerConfig?.isActive && (
        <WatchVideo
          videoId={trailerConfig.videoId}
          text={trailerConfig.text}
          textColor={trailerConfig.textColor}
        />
      )}
    </Stack>
  );
};

const SideBySide = (props: SideBySideProps) => {
  const {
    coreSectionProps,
    heroBackgroundImage,
    heroImage,
    heroImageAltText,
    imageAnimates,
    layout,
    overlapHeroTop,
    snapHeroToBottom,
    snapHeroToTop,
    textSection,
    theme,
  } = props;

  return (
    <>
      <CoreSection
        coreSectionProps={{
          backgroundColor: theme?.pageBackgroundColor,
          size: coreSectionProps?.size ? coreSectionProps?.size : '3xl',
          ...coreSectionProps,
        }}>
        <Container maxWidth={1200} size="xl">
          <Grid
            breakpoint={900}
            gapX="4xl"
            gapY="2xl"
            reverseOrder={
              layout === 'imageRight' ||
              layout === 'mobileTopDesktopRight' ||
              layout === 'mobileBottomDesktopLeft'
            }>
            {(layout === 'imageLeft' ||
              layout === 'imageRight' ||
              layout === 'mobileTopDesktopLeft' ||
              layout === 'mobileTopDesktopRight') && (
              <GridColumn span={3 / 5}>
                <Image
                  heroBackgroundImage={heroBackgroundImage}
                  heroImage={heroImage}
                  heroImageAltText={heroImageAltText}
                  imageAnimates={imageAnimates}
                  overlapHeroTop={overlapHeroTop}
                  snapHeroToBottom={snapHeroToBottom}
                  snapHeroToTop={snapHeroToTop}
                />
              </GridColumn>
            )}
            <GridColumn span={2 / 5}>
              {(textSection?.textHeader || textSection?.textContent) && (
                <TextSection {...textSection} />
              )}
            </GridColumn>
            {(layout === 'mobileBottomDesktopLeft' || layout === 'mobileBottomDesktopRight') && (
              <GridColumn span={3 / 5}>
                <Image
                  heroBackgroundImage={heroBackgroundImage}
                  heroImage={heroImage}
                  heroImageAltText={heroImageAltText}
                  imageAnimates={imageAnimates}
                  overlapHeroTop={overlapHeroTop}
                  snapHeroToBottom={snapHeroToBottom}
                  snapHeroToTop={snapHeroToTop}
                />
              </GridColumn>
            )}
          </Grid>
        </Container>
      </CoreSection>
      {(textSection?.legalText || textSection?.legalTextLine2) && (
        <LegalTextContainer
          color={`${
            textSection?.legalTextColor
              ? textSection?.legalTextColor
              : 'var(--bundle-text-color--body)'
          }`}>
          {textSection?.legalText && <p>{textSection?.legalText}</p>}
          {textSection?.legalTextLine2 && <p>{textSection?.legalTextLine2}</p>}
        </LegalTextContainer>
      )}
    </>
  );
};

export default SideBySide;
